import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import produce from 'immer'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'

import { Invoice, InvoiceItem } from '../../../../../../data/models'
import { ConfigurableTable } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'
import { AddInvoiceItemAllowanceCharge } from './AddInvoiceItemAllowanceCharge'
import { RejectInvoiceItemAllowanceCharges } from './RejectInvoiceItemAllowanceCharges'

type Props = {
  setEditModus: (params: boolean) => void
  setDisplayEditItem: (params: boolean) => void
  setDisplayEditItemAllowanceCharge: (params: boolean) => void
  setIdEditItemAllowanceCharge: (params: string) => void
  setDisplayInfoItemAllowanceCharge: (params: boolean) => void
  setIdInfoItemAllowanceCharge: (params: string) => void
  setEditActiveTabViewPanelIndex: (params: number) => void
  setInfoActiveTabViewPanelIndex: (params: number) => void
  invoiceId: string
  itemId: string
  currency: Invoice['OrderCurrency']
  data: InvoiceItem['AllowanceCharges']
}

export const AllowanceCharges: React.FC<Props> = ({
  setEditModus,
  setDisplayEditItem,
  setDisplayEditItemAllowanceCharge,
  setIdEditItemAllowanceCharge,
  setDisplayInfoItemAllowanceCharge,
  setIdInfoItemAllowanceCharge,
  setEditActiveTabViewPanelIndex,
  setInfoActiveTabViewPanelIndex,
  invoiceId,
  itemId,
  data,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  // get items for rejecting or accepting
  const [selectedItems, setSelectedItems] = useState<InvoiceItem[] | null>()

  const convertedAllowanceCharges = produce(data, draft => {
    draft?.forEach(allowanceCharge => {
      // description
      allowanceCharge.ConfirmationDescription =
        allowanceCharge.ConfirmationDescription ||
        allowanceCharge.Description?.Text
    })
  })

  const headerTitle = t(
    'invoice.invoiceItems.table.edit.dialog.allowanceCharges.table.title',
  )
  const headerActions = (
    <>
      <RejectInvoiceItemAllowanceCharges
        invoiceId={invoiceId}
        itemId={itemId}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />

      <AddInvoiceItemAllowanceCharge invoiceId={invoiceId} itemId={itemId} />
    </>
  )

  //  action template
  const actionBodyTemplate = (rowData: any) => {
    return (
      <>
        <div className="flex justify-content-end flex-nowrap">
          <Button
            icon="pi pi-info-circle"
            className="p-button-rounded p-button-text mr-2"
            onClick={() => {
              setDisplayEditItem(false)
              setIdInfoItemAllowanceCharge(rowData.Id)
              setEditModus(true)
              setDisplayInfoItemAllowanceCharge(true)
              setInfoActiveTabViewPanelIndex(1)
            }}
            visible={
              config.invoice.infoInvoiceItemAllowanceCharge.enabled !== false
            }
          />

          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => {
              setDisplayEditItem(false)
              setIdEditItemAllowanceCharge(rowData.Id)
              setDisplayEditItemAllowanceCharge(true)
              setEditActiveTabViewPanelIndex(1)
            }}
            visible={
              config.invoice.editInvoiceItemAllowanceCharge.enabled !== false
            }
          />
        </div>
      </>
    )
  }

  const buttons = <Column key="buttons" body={actionBodyTemplate}></Column>

  const isSelectable = (value: any) => {
    return value !== 'REJECTED' // if not REJECTED it is selectable
  }

  const isRowSelectable = (event: any) => {
    return isSelectable(event.data.Status)
  }

  // deactivate the row if it is REJECTED
  const rowClassName = (data: any) => {
    return isSelectable(data.Status) ? '' : 'p-disabled'
  }

  return (
    <ConfigurableTable
      config={config.invoice.editInvoiceItem.allowanceCharges.table}
      data={convertedAllowanceCharges}
      buttons={buttons}
      headerTitle={headerTitle}
      headerActions={headerActions}
      checkboxen={true}
      selected={selectedItems}
      setSelected={setSelectedItems}
      isDataSelectable={isRowSelectable}
      rowClassName={rowClassName}
      stateKey=""
    />
  )
}
